import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface ShadowScopeProps {
  /** Raw CSS text to inject only inside this shadow tree. */
  styles: string
  children: ReactNode
}

/**
 * Renders children inside a Shadow DOM root, with the given stylesheet
 * injected only inside that shadow tree.
 *
 * Used to embed third-party component libraries (e.g. Tailwind/Radix-based
 * UI kits) inside this Mantine-based app without their global resets and
 * utility classes leaking into the rest of the page, or Mantine's styles
 * leaking in and breaking their components.
 */
export function ShadowScope({ styles, children }: ShadowScopeProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null)

  useEffect(() => {
    if (!hostRef.current) return
    const root =
      hostRef.current.shadowRoot ??
      hostRef.current.attachShadow({ mode: 'open' })
    setShadowRoot(root)
  }, [])

  return (
    <div ref={hostRef}>
      {shadowRoot &&
        createPortal(
          <>
            <style>{styles}</style>
            {children}
          </>,
          shadowRoot
        )}
    </div>
  )
}
